import type {
  OauthClientAppTypeDTO,
  oauthClientCreateDTOSchema,
  oauthClientDTOSchema,
  oauthClientUpdateDTOSchema,
  oauthClientWithSecretDTOSchema,
} from '@tempo-io/id-dto';
import { parseResponse } from 'hono/client';
import type { z } from 'zod';
import { getIdApi } from '@/common/api/id/getIdApi';

export type OauthClientAppType = OauthClientAppTypeDTO;
export type OauthClient = z.infer<typeof oauthClientDTOSchema>;
export type OauthClientCreateInput = z.infer<typeof oauthClientCreateDTOSchema>;
export type OauthClientUpdateInput = z.infer<typeof oauthClientUpdateDTOSchema>;
export type OauthClientWithSecret = z.infer<typeof oauthClientWithSecretDTOSchema>;

const listOauthClients = async (): Promise<OauthClient[]> =>
  parseResponse(getIdApi().organizations.v1['oauth-clients'].$get());

const getOauthClient = async (id: string): Promise<OauthClientWithSecret> =>
  parseResponse(getIdApi().organizations.v1['oauth-clients'][':id'].$get({ param: { id } }));

const createOauthClient = async (input: OauthClientCreateInput): Promise<OauthClientWithSecret> =>
  parseResponse(getIdApi().organizations.v1['oauth-clients'].$post({ json: input }));

const updateOauthClient = async (id: string, input: OauthClientUpdateInput): Promise<OauthClient> =>
  parseResponse(
    getIdApi().organizations.v1['oauth-clients'][':id'].$patch({
      param: { id },
      json: input,
    }),
  );

const deleteOauthClient = async (id: string): Promise<void> => {
  await parseResponse(
    getIdApi().organizations.v1['oauth-clients'][':id'].$delete({ param: { id } }),
  );
};

const rotateOauthClientSecret = async (id: string): Promise<OauthClientWithSecret> =>
  parseResponse(
    getIdApi().organizations.v1['oauth-clients'][':id']['rotate-secret'].$post({
      param: { id },
    }),
  );

export const oauthClientsApi = {
  list: listOauthClients,
  get: getOauthClient,
  create: createOauthClient,
  update: updateOauthClient,
  remove: deleteOauthClient,
  rotateSecret: rotateOauthClientSecret,
};
